import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

type Mode = 'dark' | 'light';

interface Ctx {
  mode: Mode;
  toggle: () => void;
  set: (m: Mode) => void;
}

const ColorModeContext = createContext<Ctx>({ mode: 'dark', toggle: () => {}, set: () => {} });

const STORAGE_KEY = 'csre_color_mode';

const readInitial = (): Mode => {
  if (typeof window === 'undefined') return 'dark';
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === 'light' || stored === 'dark') return stored;
  return 'dark';
};

export const ColorModeProvider = ({ children }: { children: React.ReactNode }) => {
  const [mode, setMode] = useState<Mode>(readInitial);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', mode);
    try {
      window.localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // localStorage might be unavailable (private mode, SSR, etc.)
    }
  }, [mode]);

  const value = useMemo<Ctx>(
    () => ({
      mode,
      toggle: () => setMode(m => (m === 'dark' ? 'light' : 'dark')),
      set: setMode,
    }),
    [mode]
  );

  return (
    <ColorModeContext.Provider value={value}>
      {children}
    </ColorModeContext.Provider>
  );
};

export const useColorMode = () => useContext(ColorModeContext);
